import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { FaCheck, FaArrowRight } from 'react-icons/fa';
import './About.css';

// Import Assets (using placeholders/existing assets where possible)
import heroImg from '../../Assets/Services/ChassisTransport/Background.png';
import service1 from '../../Assets/Services/Background.webp';
import service2 from '../../Assets/Services/Background-1.webp';
import service3 from '../../Assets/Services/Background-2.webp';
import service4 from '../../Assets/Services/Background-3.webp';
import chairmanImg from "../../Assets/About/Chairman.png";
import team1 from "../../Assets/About/Team (1).png";
import team2 from "../../Assets/About/Team (2).png";
import team3 from "../../Assets/About/Team (1).png";
import team4 from "../../Assets/About/Team (1).png";
import user1 from "../../Assets/About/Author.png";
import user2 from "../../Assets/About/Author (1).png";
import user3 from "../../Assets/About/Author (2).png";
import user4 from "../../Assets/About/Author (3).png";

const About = () => {

    const navigate = useNavigate();

    const testimonials = [
        {
            name: "Zaire Rhiel Madsen",
            img: user1, // Replace with your imported assets
            text: "Selecting Janta Roadways as my provider for the past five years has undoubtedly proven to be a wise decision. Their meticulously crafted transport services address my needs precisely."
        },
        {
            name: "Kaylynn Workman",
            img: user2,
            text: "Choosing Janta for five years has been advantageous. Their personalized logistics solutions address my necessities precisely. Their customer service stands out — always swift."
        },
        {
            name: "Phillip Levin",
            img: user3,
            text: "I've been working with Janta Roadways for over five years now, and I couldn't be happier. They provided me with a customized transport policy that perfectly suits my business needs."
        },
        {
            name: "Olivia Anderson",
            img: user4,
            text: "In a world of choices, my allegiance to Janta has remained steadfast for over five years, a testament to the trust they've consistently earned through reliable logistics."
        }
    ];

    const services = [
        { 
            title: "Automobile Logistics", 
            img: service1, 
            link: "/services/automobile-logistics", 
            desc: "Specialized car carrier solutions designed for leading OEMs, ensuring safe, damage-free, and timely vehicle transportation across India." 
        },
        { 
            title: "Chassis & Commercial Vehicle Transport", 
            img: service2, 
            link: "/services/chassis-transport", 
            desc: "Secure and compliant transport of truck chassis, buses, tractors, and commercial vehicles using specially configured carriers." 
        },
        { 
            title: "ODC & Heavy Cargo Movement", 
            img: service3, 
            link: "/services/odc-heavy-cargo", 
            desc: "Engineered solutions for Over-Dimensional Cargo including earth movers, heavy machinery, and industrial equipment with route planning and permit management." 
        },
        { 
            title: "Mining & Surface Transport", 
            img: service4, 
            link: "/services/mining-transport", 
            desc: "Tailor-made mining logistics services for bulk movement and surface transport operations in challenging terrains and high-demand environments." 
        }
    ];

    // Animation Variants
    const fadeUp = {
        hidden: { opacity: 0, y: 50 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
    };

    const staggerContainer = {
        hidden: {},
        visible: { transition: { staggerChildren: 0.2 } }
    };

    return (
        <div className="about-page-wrapper">

            {/* 1. HERO SECTION */}
            <section className="about-hero-section">
                <Container>
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeUp}
                        className="text-center mb-5"
                    >
                        <h4 className="section-subtitle-red">ABOUT US</h4>
                        <h1 className="section-main-title">Who We Are</h1>
                        <p className="section-desc-centered">
                            Janta Roadways is the flagship company of the Janta Group, delivering reliable,
                            compliant, and safe automobile and heavy cargo transportation across India since 1982.
                        </p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1 }}
                        className="hero-image-wrapper"
                    >
                        <img src={heroImg} alt="Janta Roadways Truck" className="img-fluid w-100 rounded-3" />
                    </motion.div>
                </Container>
            </section>

            {/* 2. STATS & DEDICATION SECTION */}
            <section className="stats-dedication-section py-5">
                <Container>
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeUp}
                    >
                        <h2 className="mb-5 text-start section-heading-bold">
                            We Are Dedicated To Delivering Reliable <br />
                            Automobile Logistics Across India
                        </h2>
                    </motion.div>

                    <Row className="g-0 stats-row">
                        {/* Red Box: Stats */}
                        <Col lg={7} className="stats-col-red">
                            <motion.div
                                initial={{ x: -50, opacity: 0 }}
                                whileInView={{ x: 0, opacity: 1 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.8 }}
                                className="p-5 text-white h-100 d-flex flex-column justify-content-center"
                            >
                                <p className="mb-4 text-justify stats-text">
                                    With over four decades of experience, Janta Roadways specializes in
                                    automobile logistics, chassis transport, ODC, and heavy cargo movement.
                                    Our operations are built on safety, compliance, and consistent delivery
                                    performance for leading OEMs across India.
                                </p>
                                <div className="d-flex justify-content-between mt-4 border-top pt-4 border-white-50">
                                    <div>
                                        <h2 className="fw-bold">40K+</h2>
                                        <span className="small text-uppercase">Vehicles handled annually</span>
                                    </div>
                                    <div>
                                        <h2 className="fw-bold">40K+</h2>
                                        <span className="small text-uppercase">Years of industry experience</span>
                                    </div>
                                </div>
                            </motion.div>
                        </Col>

                        {/* White Box: Checklist */}
                        <Col lg={5} className="stats-col-white">
                            <motion.div
                                initial={{ x: 50, opacity: 0 }}
                                whileInView={{ x: 0, opacity: 1 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.8 }}
                                className="p-5 h-100 d-flex flex-column justify-content-center bg-white shadow-sm"
                            >
                                <ul className="list-unstyled checklist">
                                    <li><FaCheck className="check-icon" /> Automobile Logistics</li>
                                    <li><FaCheck className="check-icon" /> Chassis & ODC Transport</li>
                                    <li><FaCheck className="check-icon" /> CMVR-Compliant Fleet</li>
                                    <li><FaCheck className="check-icon" /> Pan-India Operations</li>
                                </ul>
                            </motion.div>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* 3. CHAIRMAN'S PROFILE */}
            <section className="chairman-section py-5 bg-light">
                <Container>
                    <Row className="align-items-center">
                        <Col lg={5}>
                            <motion.div
                                initial={{ opacity: 0, x: -50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.8 }}
                            >
                                <img src={chairmanImg} alt="Chairman" className="img-fluid rounded shadow chairman-img" />
                            </motion.div>
                        </Col>
                        <Col lg={7}>
                            <motion.div
                                initial={{ opacity: 0, x: 50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.8 }}
                                className="ps-lg-5 mt-4 mt-lg-0"
                            >
                                <h2 className="mb-4 section-heading-bold">Chairman’s Profile</h2>
                                <div className="chairman-card bg-white p-4 shadow-sm mb-4 border-start-red">
                                    <h5>Mr. Rajinder Singh</h5>
                                    <p className="mb-0 text-muted">Chairman & Managing Director – Janta Group</p>
                                </div>
                                <p className="text-muted text-justify">
                                    Mr. Rajinder Singh is the Chairman and Managing Director of the Janta Group of Companies. Since founding Janta Roadways Private Limited in 1982, he has transformed the organization from a single transport venture into a diversified group operating across multiple logistics and allied business verticals.

                                    <br /><br />
                                    With over 17 years of prior industry experience, his strong focus on safety, compliance, and operational excellence has built a foundation of trust and long-term partnerships. He has also played a key role in establishing several group companies and continues to contribute actively to the growth and development of the Indian road transport ecosystem through industry associations and leadership roles.
                                </p>
                            </motion.div>
                        </Col>
                    </Row>
                </Container>
            </section>


            {/* 4. TEAM SECTION */}
            <section className="team-section py-5">
                <Container>
                    {/* Updated Header Layout: Heading Left, Para + Button Right */}
                    <div className="row align-items-center mb-5">
                        <div className="col-lg-6">
                            <h2 className="section-heading-bold display-5 fw-bold">Meet Our Dedicated Team</h2>
                        </div>
                        <div className="col-lg-6 d-flex flex-column align-items-lg-end text-lg-end">
                            <p className="text-muted mb-4 max-w-400">
                                Our team of experienced professionals ensures secure,
                                compliant, and on-time delivery of every shipment across India.
                            </p>
                            <button className="btn btn-red px-4 py-2 text-uppercase">
                                View All Team <FaArrowRight className="ms-2" />
                            </button>
                        </div>
                    </div>

                    <motion.div
                        className="row"
                        variants={staggerContainer}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                    >
                        {[
                            { img: team1, name: "MR. MANAVDEEP SINGH", role: "Janta Group of Companies" },
                            { img: team2, name: "JOHN MITCHELL", role: "Logistics Coordinators" },
                            { img: team3, name: "WILLIAM ANDERSON", role: "Logistics Coordinators" },
                            { img: team4, name: "JOHN MAERO", role: "Route Planners" }
                        ].map((member, idx) => (
                            <Col md={3} sm={6} key={idx} className="mb-4">
                                <motion.div variants={fadeUp} className="team-card">
                                    <img src={member.img} alt={member.name} className="img-fluid w-100 rounded-4 mb-3 team-img" />
                                    <h6 className="fw-bold mb-1 text-uppercase">{member.name}</h6>
                                    <p className="text-muted small">{member.role}</p>
                                </motion.div>
                            </Col>
                        ))}
                    </motion.div>
                </Container>
            </section>

            {/* 5. SERVICES SECTION */}
        <section className="services-grid-section py-5 bg-white">
            <Container>
                <div className="text-center mb-5">
                    <h2 className="section-heading-bold display-5 fw-bold">Our Services</h2>
                    <p className="text-muted mx-auto" style={{ maxWidth: '600px' }}>
                        Whether you're looking for coverage for your home, automobile, health, or business, we've got you covered.
                    </p>
                </div>

                <Row>
                    {services.map((srv, idx) => (
                        <Col md={6} key={idx} className="mb-5">
                            <motion.div
                                className="service-card-vertical shadow-sm h-100 shadow-hover clickable-card"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: idx * 0.1 }}
                                viewport={{ once: true }}
                                onClick={() => navigate(srv.link)}
                            >
                                <div className="img-container">
                                    <img src={srv.img} alt={srv.title} className="service-main-img" />
                                </div>
                                <div className="service-content p-4">
                                    <h4 className="fw-bold mb-3">{srv.title}</h4>
                                    <p className="text-muted mb-4">{srv.desc}</p>
                                    <Link to={srv.link} className="red-link-text small fw-bold text-uppercase">
                                        Learn More <FaArrowRight className="ms-1" />
                                    </Link>
                                </div>
                                {/* Red Bottom Border Alignment */}
                                <div className="card-red-border"></div>
                            </motion.div>
                        </Col>
                    ))}
                </Row>
            </Container>
        </section>

            {/* 6. TESTIMONIALS (TRUSTED BY) */}
    <section className="testimonials-section py-5 bg-white">
            <Container>
                <div className="text-center mb-5">
                    <span className="section-subtitle-red text-uppercase fw-bold small mb-2 d-block">Testimonials</span>
                    <h2 className="section-heading-bold display-6 fw-bold">
                        Trusted By Leading OEMs <br /> & Transport Partners
                    </h2>
                </div>

                <Row>
                    {testimonials.map((item, idx) => (
                        <Col md={3} sm={6} key={idx} className="mb-4">
                            <motion.div 
                                className="testimonial-card h-100"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: idx * 0.1 }}
                                viewport={{ once: true }}
                            >
                                {/* Top Image */}
                                <div className="user-thumb mb-4">
                                    <img src={item.img} alt={item.name} className="rounded-circle shadow-sm" />
                                </div>
                                
                                {/* Feedback Text */}
                                <p className="testimonial-text text-muted mb-5">
                                    {item.text}
                                </p>
                                
                                {/* Client Name */}
                                <h6 className="client-name fw-bold mt-auto">{item.name}</h6>
                            </motion.div>
                        </Col>
                    ))}
                </Row>
            </Container>
        </section>

        </div>
    );
};

export default About;
